import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  useFormContext,
  type FormControl,
} from 'platform/components';
import {Grid, Show, Text, VStack} from 'platform/foundation';
import {css} from 'styled-components';

import {useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {getFeatureCategoryOptions, SelectableCard} from '@dms/features/new-vehicle';

import type {Nullish} from 'shared/web';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {joinParts, t} from '../utils/catalogueUi';
import {
  equipmentDraftSchema,
  emptyEquipmentDraft,
  type EquipmentDraft,
} from '../utils/equipmentDraft';
import {
  missingPriceFills,
  optionalPriceError,
  optionPriceRequired,
  priceInput,
} from '../utils/moneyInput';
import {GrossPriceField} from './GrossPriceField';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';

type Inclusion = EquipmentDraft['inclusion'];
type ExistingInclusions = Record<string, Inclusion>;

const optionalTrimIds = (
  values: Pick<EquipmentDraft, 'standaloneOrderable' | 'inclusion'> & {trimIds: string[] | Nullish},
  trims: {id: string}[],
  existingInclusions: ExistingInclusions | undefined
) =>
  values.standaloneOrderable
    ? trims
        .map(({id}) => id)
        .filter(
          (id) =>
            (values.trimIds ?? []).includes(id) &&
            (existingInclusions?.[id] ?? values.inclusion) === 'OPTIONAL'
        )
    : [];

const STEP_ONE_FIELDS: (keyof EquipmentDraft)[] = [
  'name',
  'equipmentType',
  'standaloneOrderable',
  'category',
  'factoryCode',
];

interface EquipmentCarScope {
  makes: {id: string; name: string}[];
  models: {id: string; name: string; makeId: string}[];
  modelYears: {id: string; name: string; modelId: string}[];
}

const equipmentTypes = (): {
  value: EquipmentDraft['equipmentType'];
  label: string;
  description: string;
}[] => [
  {
    value: 'factory-option',
    label: t('entity.newVehicle.authoring.factoryOption', 'Factory option'),
    description: t(
      'entity.newVehicle.authoring.factoryOptionDescription',
      'Fitted at the factory and available to order on its own.'
    ),
  },
  {
    value: 'dealer-accessory',
    label: t('entity.newVehicle.authoring.dealerAccessory', 'Dealer accessory'),
    description: t(
      'entity.newVehicle.authoring.dealerAccessoryDescription',
      'Fitted by the dealer after the vehicle leaves the factory.'
    ),
  },
  {
    value: 'wheel',
    label: t('entity.newVehicle.authoring.wheel', 'Wheel'),
    description: t(
      'entity.newVehicle.authoring.wheelDescription',
      'A factory or dealer-fitted wheel and tyre choice.'
    ),
  },
];

function EquipmentIdentity(props: {control: FormControl<EquipmentDraft>; isDisabled?: boolean}) {
  const type = useWatch({control: props.control, name: 'equipmentType'});
  const standaloneOrderable = useWatch({
    control: props.control,
    name: 'standaloneOrderable',
  });
  const form = useFormContext<EquipmentDraft>();
  return (
    <VStack spacing={4}>
      <FormField
        control={props.control}
        name="name"
        isDisabled={props.isDisabled}
        type="text"
        label={t('general.labels.name', 'Name')}
        isRequired
      />
      <VStack spacing={2}>
        <Text size="small">{t('general.labels.type', 'Type')}</Text>
        {equipmentTypes().map((option) => (
          <SelectableCard
            key={option.value}
            control="radio"
            name="equipmentType"
            title={option.label}
            description={option.description}
            isSelected={type === option.value}
            isDisabled={props.isDisabled}
            onSelect={() => {
              form.setValue('equipmentType', option.value, {shouldDirty: true});
              // BE rejects a wheel set outside wheels_tyres (DMS-NV-CT-0038).
              if (option.value === 'wheel') {
                form.setValue('category', 'wheels_tyres', {shouldDirty: true});
              }
              props.control.onChange();
            }}
          />
        ))}
      </VStack>
      <Show when={type === 'dealer-accessory'}>
        <Alert
          variant="info"
          message={t(
            'entity.newVehicle.catalogue.dealerFitHelp',
            'Dealer-fit equipment is never locked with the factory specification.'
          )}
        />
      </Show>
      <Show when={!standaloneOrderable}>
        <Alert
          variant="info"
          message={t(
            'entity.newVehicle.authoring.packageOnlyHelp',
            'Sold through packages, never as a standalone option. It may be included as standard equipment. The price belongs to the package.'
          )}
        />
      </Show>
      <Grid columns={[1, 1, 2]} spacing={4}>
        <FormField
          control={props.control}
          name="category"
          isDisabled={props.isDisabled || type === 'wheel'}
          type="choice"
          menuInPortal
          label={t('entity.newVehicle.catalogue.category', 'Category')}
          isRequired
          isNotClearable
          options={getFeatureCategoryOptions(t)}
        />
        <FormField
          control={props.control}
          name="factoryCode"
          isDisabled={props.isDisabled}
          type="text"
          label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
        />
      </Grid>
    </VStack>
  );
}

function EquipmentScope(props: {
  control: FormControl<EquipmentDraft>;
  canViewCost: boolean;
  trims: {id: string; name: string; modelYearId?: string}[];
  scopeLabel: string;
  ownershipMessage: string;
  carScope?: EquipmentCarScope;
  existingInclusions?: ExistingInclusions;
  isTrimRequired?: boolean;
  isDisabled?: boolean;
}) {
  const type = useWatch({control: props.control, name: 'equipmentType'});
  const standaloneOrderable = useWatch({
    control: props.control,
    name: 'standaloneOrderable',
  });
  const selected = useWatch({control: props.control, name: 'trimIds'});
  const scope = useWatch({control: props.control, name: 'scope'});
  const name = useWatch({control: props.control, name: 'name'});
  const inclusion = useWatch({control: props.control, name: 'inclusion'});
  const form = useFormContext<EquipmentDraft>();
  const vatRate = useCatalogueVatRate(scope?.modelYearId);
  const trims = props.carScope
    ? props.trims.filter((trim) => trim.modelYearId === scope?.modelYearId)
    : props.trims;
  const isPreservingInclusions = isNotNil(props.existingInclusions);
  const optionalIds = optionalTrimIds(
    {standaloneOrderable, trimIds: selected, inclusion},
    trims,
    props.existingInclusions
  );
  const prefillPrices = () => {
    const ids = optionalTrimIds(form.getValues(), trims, props.existingInclusions);
    Object.entries(missingPriceFills(form.getValues('trimPrices'), ids)).forEach(([id, price]) =>
      form.setValue(`trimPrices.${id}`, price, {shouldDirty: true})
    );
  };
  return (
    <VStack spacing={4}>
      <Text alternative>
        {joinParts([name, equipmentTypes().find((option) => option.value === type)?.label])}
      </Text>
      <Text>{props.scopeLabel}</Text>
      <Alert variant="info" message={props.ownershipMessage} />
      <Show when={!!props.carScope}>
        <Grid columns={[1, 3, 3]} spacing={3}>
          <FormField
            control={props.control}
            name="scope.makeId"
            isDisabled={props.isDisabled}
            type="choice"
            menuInPortal
            label={t('entity.vehicle.labels.make', 'Make')}
            isNotClearable
            options={
              props.carScope?.makes.map((make) => ({value: make.id, label: make.name})) ?? []
            }
            onChange={() => {
              form.setValue('scope.modelId', '');
              form.setValue('scope.modelYearId', '');
              form.setValue('trimIds', []);
              props.control.onChange();
            }}
          />
          <FormField
            control={props.control}
            name="scope.modelId"
            isDisabled={props.isDisabled}
            type="choice"
            menuInPortal
            label={t('entity.vehicle.labels.model', 'Model')}
            isNotClearable
            options={
              props.carScope?.models
                .filter((model) => model.makeId === scope?.makeId)
                .map((model) => ({value: model.id, label: model.name})) ?? []
            }
            onChange={() => {
              form.setValue('scope.modelYearId', '');
              form.setValue('trimIds', []);
              props.control.onChange();
            }}
          />
          <FormField
            control={props.control}
            name="scope.modelYearId"
            isDisabled={props.isDisabled}
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.labels.modelYear', 'Model year')}
            isNotClearable
            options={
              props.carScope?.modelYears
                .filter((year) => year.modelId === scope?.modelId)
                .map((year) => ({value: year.id, label: year.name})) ?? []
            }
            onChange={() => {
              form.setValue('trimIds', []);
              props.control.onChange();
            }}
          />
        </Grid>
      </Show>
      <FormField
        control={props.control}
        name="trimIds"
        isDisabled={props.isDisabled}
        type="multiChoice"
        menuInPortal
        label={t('entity.newVehicle.catalogue.trimLevels', 'Trim levels')}
        isRequired={props.isTrimRequired}
        options={trims.map((row) => ({value: row.id, label: row.name}))}
        onChange={prefillPrices}
      />
      <Show when={standaloneOrderable}>
        <FormField
          control={props.control}
          name="inclusion"
          isDisabled={props.isDisabled}
          type="choice"
          menuInPortal
          label={
            isPreservingInclusions
              ? t('entity.newVehicle.authoring.newAttachmentsAs', 'New attachments added as')
              : t('entity.newVehicle.authoring.attachToTrimsAs', 'Attach to trims as')
          }
          isNotClearable
          options={[
            {
              value: 'STANDARD',
              label: t('entity.newVehicle.authoring.standard', 'Standard — included with the trim'),
            },
            {
              value: 'OPTIONAL',
              label: t('entity.newVehicle.authoring.optional', 'Optional — standalone add-on'),
            },
          ]}
          onChange={prefillPrices}
        />
      </Show>
      {trims
        .filter(({id}) => optionalIds.includes(id))
        .map((trim) => (
          <PriceRow
            key={trim.id}
            vatRate={vatRate}
            canViewCost={false}
            spacing={4}
            stackBelow="notebook"
          >
            <GrossPriceField
              control={props.control}
              name={`trimPrices.${trim.id}`}
              vatRate={vatRate}
              isDisabled={props.isDisabled}
              label={t(
                'entity.newVehicle.catalogue.priceOnTrimNet',
                'Price of {feature} on {trim}, net',
                {feature: name, trim: trim.name}
              )}
              helperText={optionPriceRequired()}
              onBlur={prefillPrices}
            />
          </PriceRow>
        ))}
      {/* Every attachment carries the cost, Standard ones too (D51), so the saved cost is always shown. */}
      <Show when={props.canViewCost && selected.length > 0}>
        <Grid columns={[1, 1, 2]} spacing={4}>
          <NumberField
            control={props.control}
            name="costNet"
            isDisabled={props.isDisabled}
            label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
          />
        </Grid>
      </Show>
      <Show when={isPreservingInclusions && standaloneOrderable}>
        <Text>
          {t(
            'entity.newVehicle.authoring.preserveInclusions',
            'Existing trims keep their individual Standard / Optional inclusion. Change those assignments on the Equipment step.'
          )}
        </Text>
      </Show>
      <Show when={!standaloneOrderable}>
        <Text>
          {t(
            'entity.newVehicle.authoring.standardOnly',
            'Standard equipment only. Add this item to a package to sell it as part of that package.'
          )}
        </Text>
      </Show>
      <Show when={selected.length === 0 && !props.isTrimRequired}>
        <Text>
          {t(
            'entity.newVehicle.authoring.libraryOnly',
            'No trims selected: this item will be saved to the equipment library only.'
          )}
        </Text>
      </Show>
    </VStack>
  );
}

export function NewEquipmentFlow(props: {
  canViewCost: boolean;
  trims: {id: string; name: string; modelYearId?: string}[];
  scopeLabel: string;
  ownershipMessage: string;
  carScope?: EquipmentCarScope;
  initialValues?: EquipmentDraft;
  errorMessage?: string;
  isSaveDisabled?: boolean;
  isIntentLocked?: boolean;
  /** Editing an item already on trims: those trims keep their own inclusion. */
  existingInclusions?: ExistingInclusions;
  isIdentityLocked?: boolean;
  /** No "library only" save: the item must land on at least one trim. */
  isTrimRequired?: boolean;
  onSave: (draft: EquipmentDraft) => void | Promise<void>;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  return (
    <Dialog
      isOpen
      onClose={props.onClose}
      withAdditionalFooter
      title={
        step === 1
          ? t(
              'entity.newVehicle.authoring.equipmentStepOne',
              'New equipment · 1 of 2 — What is it?'
            )
          : t(
              'entity.newVehicle.authoring.equipmentStepTwo',
              'New equipment · 2 of 2 — For which cars?'
            )
      }
    >
      <Form<EquipmentDraft>
        experimentalZodSchema={equipmentDraftSchema}
        defaultValues={props.initialValues ?? emptyEquipmentDraft}
        onChange={(values, _setErrors, _reset, form) => {
          if (values.category === null) {
            form.setValue('category', '');
          }
          for (const name of ['name', 'factoryCode', 'costNet'] as const) {
            if (values[name] === null) {
              form.setValue(name, '');
            }
          }
          if (values.trimIds === null) {
            form.setValue('trimIds', []);
          }
          const invalidStepOneFields = STEP_ONE_FIELDS.filter(
            (name) => form.getFieldState(name).invalid
          );
          if (invalidStepOneFields.length > 0) {
            void form.trigger(invalidStepOneFields);
          }
        }}
        onSubmit={async (values, setErrors) => {
          if (step === 1) {
            setStep(2);
            return;
          }
          if (props.isTrimRequired && values.trimIds.length === 0) {
            setErrors([
              {
                name: 'trimIds',
                message: t('entity.newVehicle.authoring.selectTrim', 'Select at least one trim.'),
              },
            ]);
            return;
          }
          const pricedTrimIds = optionalTrimIds(values, props.trims, props.existingInclusions);
          const priceErrors = pricedTrimIds.flatMap((trimId) => {
            const message =
              isNilOrEmpty(values.trimPrices[trimId]) && isNil(props.existingInclusions?.[trimId])
                ? optionPriceRequired()
                : optionalPriceError(values.trimPrices[trimId]);
            return isNil(message) ? [] : [{name: `trimPrices.${trimId}`, message}];
          });
          if (priceErrors.length > 0) {
            setErrors(priceErrors);
            return;
          }
          await props.onSave({
            ...values,
            trimPrices: Object.fromEntries(
              pricedTrimIds
                .filter((trimId) => !isNilOrEmpty(values.trimPrices[trimId]))
                .map((trimId) => [trimId, priceInput.parse(values.trimPrices[trimId])])
            ),
            costNet: props.canViewCost ? values.costNet : '',
            inclusion: values.standaloneOrderable ? values.inclusion : 'STANDARD',
          });
        }}
      >
        {(control, form) => (
          <VStack spacing={6}>
            <Show when={!!props.errorMessage}>
              <Alert variant="warning" message={props.errorMessage ?? ''} />
            </Show>
            <fieldset
              disabled={props.isIntentLocked}
              // eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
              css={css`
                border: 0;
                margin: 0;
                padding: 0;
                min-width: 0;
              `}
            >
              {step === 1 ? (
                <EquipmentIdentity
                  control={control}
                  isDisabled={props.isIntentLocked || props.isIdentityLocked}
                />
              ) : (
                <EquipmentScope
                  control={control}
                  canViewCost={props.canViewCost}
                  isDisabled={props.isIntentLocked}
                  trims={props.trims}
                  scopeLabel={props.scopeLabel}
                  ownershipMessage={props.ownershipMessage}
                  carScope={props.carScope}
                  existingInclusions={props.existingInclusions}
                  isTrimRequired={props.isTrimRequired}
                />
              )}
            </fieldset>
            <DialogFooter data-testid="newVehicleCatalogue-newEquipment">
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={t('general.actions.cancel', 'Cancel')}
                  onClick={props.onClose}
                />
                <Show when={step === 2}>
                  <Button
                    variant="secondary"
                    title={t('general.actions.back', 'Back')}
                    onClick={() => setStep(1)}
                  />
                </Show>
                <Show when={step === 2 && !props.isTrimRequired}>
                  <FormButton
                    control={control}
                    type="button"
                    variant="secondary"
                    title={t('entity.newVehicle.authoring.skipLibraryOnly', 'Skip — library only')}
                    isDisabled={props.isIntentLocked || props.isSaveDisabled}
                    onClick={() => {
                      form.setValue('trimIds', []);
                      void control.submitForm();
                    }}
                  />
                </Show>
                {step === 1 ? (
                  <Button
                    variant="primary"
                    title={t('general.actions.continue', 'Continue')}
                    onClick={() =>
                      void form
                        .trigger(STEP_ONE_FIELDS, {shouldFocus: true})
                        .then((isValid) => isValid && setStep(2))
                    }
                  />
                ) : (
                  <FormButton
                    control={control}
                    type="submit"
                    isDisabled={props.isSaveDisabled}
                    title={t('entity.newVehicle.authoring.saveEquipment', 'Save equipment')}
                  />
                )}
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}
